import type { StrokeData } from "../site/bishun-drawing.js";
import { fetchStrokeFile } from "../site/stroke-file.js";
import {
  canonicalAddress,
  canonicalEntry,
} from "../segmenting/canonical-entry.js";

/**
 * What the site publishes about the characters on a sheet.
 *
 * Every answer a sheet prints comes from here: the reading and first sense
 * from the 词典 entry's `canonical.json`, and the strokes from the outlines the
 * 笔顺 figure draws. Nothing on a sheet is typed in except the words chosen,
 * which is the difference from every other worksheet generator on the web.
 */

/** A 词典 entry, as much of it as a sheet prints. */
export interface SheetEntry {
  readonly word: string;

  /** Its reading as the entry writes it, every reading of a 多音字 included. */
  readonly pinyin: string;

  /** The reading of each character, in order, where the entry marks them. */
  readonly syllables: readonly string[];

  /**
   * Each of the entry's readings, a syllable and a tone at a time, where the
   * entry marks them. A 多音字 has several, 行 three and 东西 two.
   */
  readonly readings: readonly SheetReading[];

  /** The first English sense. */
  readonly meaning: string;
}

/** One reading of an entry, as its syllables and their tones. */
export interface SheetReading {
  /** Each syllable as the entry writes it, marks and any apostrophe included. */
  readonly syllables: readonly string[];

  /** Each syllable's tone, 1 to 4, and 5 for the neutral tone. */
  readonly tones: readonly number[];
}

/** What a sheet needs to read entries and outlines. */
export interface WorksheetEntriesProperties {
  readonly fetch: typeof fetch;

  /** Where the outlines are published, `data.strokes` in `assets.json`. */
  readonly strokesUrl: string;
}

/**
 * The syllables an entry's reading marks, one span each.
 *
 * `reading` is the tone-coloured markup the entry page draws, a
 * `py-syllable` span per syllable. A 多音字's readings are separated by a
 * comma outside the spans, and are read as a list of single syllables.
 */
export function readingSyllables(reading: string): readonly string[] {
  return [
    ...reading.matchAll(/<span[^>]*\bpy-syllable\b[^>]*>([^<]*)<\/span>/gu),
    // The pattern's one group always takes part in a match.
  ].map((match) => match[1]!);
}

/**
 * An entry's readings, from the markup its `reading` carries.
 *
 * Each span names its tone in a `py-tone-N` class, which is what the 词典
 * colours the syllable by, so the tones a pinyin sheet asks for are the 词典's
 * own and not a converter's guess from the marks. The readings of a 多音字 are
 * separated by a comma outside the spans, and no span or attribute holds one.
 */
export function readingsOf(reading: string): readonly SheetReading[] {
  return reading
    .split(",")
    .map((part) => {
      const spans = [
        ...part.matchAll(/<span([^>]*\bpy-syllable\b[^>]*)>([^<]*)<\/span>/gu),
      ];

      return {
        // Both of the pattern's groups take part in every match.
        syllables: spans.map((span) => span[2]!),
        tones: spans.map((span) =>
          Number(/\bpy-tone-([1-5])\b/u.exec(span[1]!)?.[1] ?? 5),
        ),
      };
    })
    .filter((parsed) => parsed.syllables.length > 0);
}

/**
 * Reads entries and outlines, each one once.
 *
 * A promise is kept per address rather than an answer, so two characters that
 * ask for the same word at once share the one request.
 */
export class WorksheetEntries {
  readonly #fetch: typeof fetch;
  readonly #strokesUrl: string;
  readonly #entries = new Map<string, Promise<SheetEntry | undefined>>();
  readonly #strokes = new Map<string, Promise<StrokeData | undefined>>();

  constructor(properties: WorksheetEntriesProperties) {
    this.#fetch = properties.fetch;
    this.#strokesUrl = properties.strokesUrl;
  }

  /** The 词典 entry for a word or a character, or nothing where there is none. */
  entry(word: string): Promise<SheetEntry | undefined> {
    let entry = this.#entries.get(word);
    if (entry === undefined) {
      entry = this.#readEntry(word);
      this.#entries.set(word, entry);
    }

    return entry;
  }

  /** A character's outlines, or nothing for one the data does not draw. */
  strokes(character: string): Promise<StrokeData | undefined> {
    let strokes = this.#strokes.get(character);
    if (strokes === undefined) {
      strokes = fetchStrokeFile(this.#strokesUrl, character, this.#fetch).then(
        (data) =>
          data !== undefined && Array.isArray(data.strokes) ? data : undefined,
      );
      this.#strokes.set(character, strokes);
    }

    return strokes;
  }

  async #readEntry(word: string): Promise<SheetEntry | undefined> {
    try {
      const response = await this.#fetch(canonicalAddress(word));
      if (!response.ok) {
        return undefined;
      }
      const body: unknown = await response.json();
      const entry = canonicalEntry(body);
      if (entry === undefined) {
        return undefined;
      }
      const reading = (body as Record<string, unknown>)["reading"];

      return {
        word: entry.word,
        pinyin: entry.pinyin,
        syllables: typeof reading === "string" ? readingSyllables(reading) : [],
        readings: typeof reading === "string" ? readingsOf(reading) : [],
        meaning: entry.meanings[0] ?? "",
      };
    } catch {
      return undefined;
    }
  }
}
